import { Component, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { t } from '@/lib/i18n';
import { reportError } from '@/lib/error-tracker';
import { attemptDynamicChunkRecovery, createIncidentId } from '@/lib/runtime-recovery';

interface Props {
  children: ReactNode;
  resetKey?: string;
  routerAvailable?: boolean;
}

interface State {
  hasError: boolean;
  incidentId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, incidentId: null };

  static getDerivedStateFromError(): State {
    return { hasError: true, incidentId: createIncidentId() };
  }

  componentDidCatch(err: Error, info: { componentStack?: string | null }) {
    reportError(err, info, { requestId: this.state.incidentId ?? undefined });
    attemptDynamicChunkRecovery(err);
  }

  componentDidUpdate(previous: Props) {
    if (this.state.hasError && previous.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, incidentId: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="grid min-h-dvh place-items-center bg-[var(--bg)] p-4 sm:p-6">
          <section
            className="w-full max-w-lg rounded-[var(--r-xl)] border border-[var(--border)] bg-[var(--surface)] p-5 shadow-[var(--shadow-sm)] sm:p-7"
            role="alert"
            aria-labelledby="app-error-title"
          >
            <AlertTriangle className="h-6 w-6 text-[var(--danger)]" aria-hidden="true" />
            <h1 id="app-error-title" className="mt-4 text-xl font-semibold text-[var(--text)]">
              {t('common.something_broke')}
            </h1>
            <p className="mt-2 text-sm leading-6 text-[var(--text-muted)]">
              Экран не удалось открыть. Попробуй восстановить его или вернись на главную.
            </p>
            {this.state.incidentId && (
              <p className="mt-3 break-all font-mono text-xs text-[var(--text-muted)]">
                Код ошибки: {this.state.incidentId}
              </p>
            )}
            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button
                type="button"
                variant="primary"
                onClick={() => this.setState({ hasError: false, incidentId: null })}
              >
                {t('common.retry')}
              </Button>
              {this.props.routerAvailable ? (
                <Button asChild variant="secondary">
                  <Link to="/">{t('common.go_home')}</Link>
                </Button>
              ) : (
                <Button asChild variant="secondary">
                  <a href="/">{t('common.go_home')}</a>
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={() => window.location.reload()}>
                {t('common.reload')}
              </Button>
            </div>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKey={`${location.pathname}${location.search}`} routerAvailable>
      {children}
    </ErrorBoundary>
  );
}
